import type { AgentEvent, ChatMessage, StepCode, Tier, ToolStatus } from "@/domain/types";

const TOOL_STATUSES: readonly string[] = ["started", "finished", "failed", "retry"] satisfies ToolStatus[];
const MESSAGE_ROLES: readonly string[] = ["user", "agent", "system"] satisfies ChatMessage["role"][];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

/** A chat message the screen can show: it has an id, a known role and text. */
function isChatMessage(value: unknown): value is ChatMessage {
  return isRecord(value) && typeof value.id === "string" && typeof value.role === "string" && MESSAGE_ROLES.includes(value.role) && typeof value.text === "string";
}

/**
 * Reads one streamed line of agent-turn as an event. Anything unknown or malformed gives null, so the caller ignores it
 * instead of crashing on a field that is not there.
 */
export function parseAgentEvent(raw: unknown): AgentEvent | null {
  if (!isRecord(raw)) return null;
  switch (raw.type) {
    case "user":
    case "done":
    case "error":
      return isChatMessage(raw.message) ? { type: raw.type, message: raw.message } : null;
    case "status":
      if (typeof raw.tier !== "string") return null;
      return { type: "status", tier: raw.tier as Tier, step: raw.step as StepCode, model: typeof raw.model === "string" ? raw.model : undefined };
    case "delta":
      return typeof raw.text === "string" ? { type: "delta", text: raw.text } : null;
    case "tool":
      if (typeof raw.call_id !== "string" || typeof raw.name !== "string" || typeof raw.status !== "string" || !TOOL_STATUSES.includes(raw.status)) return null;
      return { type: "tool", call_id: raw.call_id, name: raw.name, status: raw.status as ToolStatus, detail: typeof raw.detail === "string" ? raw.detail : "" };
    default:
      return null;
  }
}
